import React, { useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import SkillLifecycleTransferField from "../SkillLifecycleTransferField";
import LeadTypeCascadeSelect from "../LeadTypeCascadeSelect";
import {
  lifecycleTransferPatchFromLeadTypes,
  lifecycleTransferTypesFromConfig,
} from "../../utils/leadTypeCascade";
import {
  DETECTION_MODE_OPTIONS,
  STATUS_OPTIONS,
  getLifecycleFromConfig,
  mergeLifecycleEvent,
  translateLifecycle,
  lifecycleEventsForSkill,
  lifecycleTextKey,
  transferNeedsLeadType,
  isInactivityLifecycleEvent,
  DEFAULT_IDLE_MINUTES,
  MIN_IDLE_MINUTES,
  MAX_IDLE_MINUTES,
} from "../../utils/skillLifecycle";

/**
 * Regras de ciclo de vida por skill: transferência e status por fase do atendimento.
 */
export default function SkillLifecycleRules({
  skillKey = null,
  skillConfig = {},
  onChange,
  disabled = false,
  currentAgentId = null,
  embedded = false,
}) {
  const { t } = useTranslation("settings");
  const lifecycle = useMemo(() => getLifecycleFromConfig(skillConfig), [skillConfig]);
  const { data: usersData, isLoading: loadingUsers } = useUsers(1, 200, "", "todos");
  const { data: agentsData, isLoading: loadingAgents } = useAgents(1, 100, "");

  const copy = useCallback(
    (suffix, fallbackKey) => translateLifecycle(t, skillKey, suffix, fallbackKey),
    [t, skillKey]
  );

  const visibleEvents = useMemo(() => lifecycleEventsForSkill(skillKey), [skillKey]);

  const users = usersData?.users ?? [];
  const aiAgents = useMemo(() => {
    const rows = agentsData?.agents ?? [];
    return rows.filter(
      (a) =>
        String(a.id) !== String(currentAgentId || "") &&
        (a.status === "active" || a.status === true)
    );
  }, [agentsData?.agents, currentAgentId]);

  const shortcutLabels = useMemo(
    () => ({
      queue: t("agentsListPage.lifecycleTransferQueue"),
      receptionist: t("agentsListPage.lifecycleTransferReceptionist"),
      shortcutsGroup: t("agentsListPage.lifecycleTransferShortcuts", { defaultValue: "Atalhos" }),
    }),
    [t]
  );

  const patchEvent = (event, partial) => {
    // Updater funcional: evita closure stale apagar logged_users_only após o checkbox.
    onChange?.((prevConfig) => {
      const latestLifecycle = getLifecycleFromConfig(prevConfig);
      return { lifecycle: mergeLifecycleEvent(latestLifecycle, event, partial) };
    });
  };

  return (
    <Box>
      {!embedded && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {copy("sectionTitle", "agentsListPage.lifecycleSectionTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {copy("sectionHint", "agentsListPage.lifecycleSectionHint")}
          </Typography>
        </>
      )}
      <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: 13 }}>
        {copy("sectionNote", "agentsListPage.lifecycleSectionNote")}
      </Alert>

      <Stack spacing={1}>
        {visibleEvents.map((event) => {
          const rule = lifecycle[event];
          const eventLabel = copy(`event.${event}`, `agentsListPage.lifecycleEvent${eventToLegacyKey(event)}`);
          const eventHint = copy(`hint.${event}`, `agentsListPage.lifecycleEvent${eventToLegacyKey(event)}Hint`);
          const messageExampleKey = lifecycleTextKey(skillKey, `messageExample.${event}`);
          const messagePlaceholderRaw = t(messageExampleKey);
          const messagePlaceholder =
            messagePlaceholderRaw !== messageExampleKey ? messagePlaceholderRaw : undefined;

          return (
            <Accordion
              key={event}
              variant="outlined"
              disableGutters
              sx={{
                "&:before": { display: "none" },
                borderRadius: "12px !important",
                overflow: "hidden",
                borderColor: rule.enabled ? "primary.light" : "divider",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75 } }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", pr: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                    {eventLabel}
                  </Typography>
                  <Switch
                    size="small"
                    checked={!!rule.enabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => patchEvent(event, { enabled: e.target.checked })}
                    disabled={disabled}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    {eventHint}
                  </Typography>

                  {isInactivityLifecycleEvent(event) ? (
                    <TextField
                      label={t("agentsListPage.lifecycleIdleMinutesLabel", {
                        defaultValue: "Minutos sem resposta do cliente",
                      })}
                      helperText={t("agentsListPage.lifecycleIdleMinutesHelper", {
                        defaultValue:
                          "Após a última mensagem do agente, se o cliente não responder neste intervalo, a regra dispara. Mínimo: 5 minutos. O disparo ocorre em até cerca de 1 minuto após esse tempo.",
                      })}
                      type="number"
                      inputProps={{ min: MIN_IDLE_MINUTES, max: MAX_IDLE_MINUTES, step: 1 }}
                      value={rule.idle_minutes ?? DEFAULT_IDLE_MINUTES}
                      onChange={(e) => patchEvent(event, { idle_minutes: e.target.value })}
                      fullWidth
                      size="small"
                      disabled={disabled || !rule.enabled}
                    />
                  ) : (
                    <>
                      <FormControl fullWidth size="small" disabled={disabled || !rule.enabled}>
                        <InputLabel>{t("agentsListPage.lifecycleDetectionModeLabel")}</InputLabel>
                        <Select
                          label={t("agentsListPage.lifecycleDetectionModeLabel")}
                          value={rule.detection_mode || "auto"}
                          onChange={(e) => patchEvent(event, { detection_mode: e.target.value })}
                        >
                          {DETECTION_MODE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {t(opt.labelKey)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {(rule.detection_mode === "keywords" || rule.detection_mode === "auto_or_keywords") && (
                        <TextField
                          label={t("agentsListPage.lifecycleKeywordsLabel")}
                          helperText={copy("keywordsHelper", "agentsListPage.lifecycleKeywordsHelper")}
                          placeholder={copy("keywordsPlaceholder", "agentsListPage.lifecycleKeywordsPlaceholder")}
                          value={rule.trigger_keywords || ""}
                          onChange={(e) => patchEvent(event, { trigger_keywords: e.target.value })}
                          fullWidth
                          size="small"
                          disabled={disabled || !rule.enabled}
                        />
                      )}
                    </>
                  )}

                  <FormControl fullWidth size="small" disabled={disabled || !rule.enabled}>
                    <InputLabel>{t("agentsListPage.lifecycleStatusLabel")}</InputLabel>
                    <Select
                      label={t("agentsListPage.lifecycleStatusLabel")}
                      value={rule.status === null || rule.status === undefined ? "" : rule.status}
                      onChange={(e) => {
                        const v = e.target.value;
                        patchEvent(event, { status: v === "" ? null : Number(v) });
                      }}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={String(opt.value)} value={opt.value}>
                          {t(opt.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <SkillLifecycleTransferField
                    label={t("agentsListPage.lifecycleTransferLabel")}
                    helperText={copy("transferHelper", "agentsListPage.lifecycleTransferHelper")}
                    transfer={rule.transfer}
                    users={users}
                    agents={aiAgents}
                    excludeAgentIds={currentAgentId ? [currentAgentId] : []}
                    shortcutLabels={shortcutLabels}
                    loading={loadingUsers || loadingAgents}
                    disabled={disabled || !rule.enabled}
                    onChange={(nextTransfer) =>
                      patchEvent(event, {
                        transfer: nextTransfer,
                      })
                    }
                  />

                  {transferNeedsLeadType(rule.transfer?.dest) && (
                    <LeadTypeCascadeSelect
                      value={lifecycleTransferTypesFromConfig(rule.transfer)}
                      onChange={(types) =>
                        patchEvent(event, {
                          // Só tipos — mergeLifecycleEvent preserva logged_users_only do estado atual.
                          transfer: lifecycleTransferPatchFromLeadTypes(types),
                        })
                      }
                      disabled={disabled || !rule.enabled}
                      tipoLabel={t("agentsListPage.lifecycleLeadTypeLabel", {
                        defaultValue: "Tipo de atendimento",
                      })}
                      helperText={t("agentsListPage.lifecycleLeadTypeHelper", {
                        defaultValue: "Aplicado ao lead ao transferir neste evento.",
                      })}
                    />
                  )}

                  <TextField
                    label={t("agentsListPage.lifecycleMessageLabel")}
                    helperText={t("agentsListPage.lifecycleMessageHelper")}
                    placeholder={messagePlaceholder}
                    value={rule.message || ""}
                    onChange={(e) => patchEvent(event, { message: e.target.value })}
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    disabled={disabled || !rule.enabled}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}

function eventToLegacyKey(event) {
  const map = {
    on_start: "Start",
    on_success: "Success",
    on_failure: "Failure",
    on_escalation: "Escalation",
    on_outside_hours: "OutsideHours",
    on_inactivity: "Inactivity",
  };
  return map[event] || "Start";
}
